import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Grid,
  GridItem,
  FormControl,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
  useColorMode,
  Text,
  FormErrorMessage,
} from '@chakra-ui/react';
import '../../../../../theme/theme';
import { useDispatch, useSelector } from 'react-redux';
import { getItem } from '../../../../../redux/actions/item';
import { useUser, useGlobal } from 'contexts';
import { addScreen, updateScreen, getScreen } from '../../../../../redux/actions/screen';
import { renderDynamicDropdown } from 'components/UIcomponents/DynamicDropdown';
import { DynamicInput } from 'components/UIcomponents/Dynamicinput';
import { renderDynamicDropdown as renderDynamicUiDropdown } from '../../../../../components/ui/DynamicDropdown';
import { getShareCompanyOptions } from '../../../../../utils/util';

const FloatingLabel = ({ id, label, isFocused, hasValue, colorMode }) => {
  const isFloated = isFocused || hasValue;
  const bgColor = colorMode === 'dark' ? '#2d3748' : 'white';
  const textColor = colorMode === 'dark' ? 'white' : '#1a202c';

  return (
    <label
      htmlFor={id}
      style={{
        position: 'absolute',
        top: isFloated ? '-10px' : '15px',
        left: '15px',
        transform: isFloated
          ? 'translateX(10px) scale(0.75)'
          : 'translateX(0) scale(1)',
        color: textColor,
        transition: 'transform 200ms, top 200ms, color 200ms',
        pointerEvents: 'none',
        zIndex: 100,
        height: '25px',
        width: 'auto',
        backgroundColor: bgColor,
        borderRadius: '2vh',
        display: 'inline-block',
        paddingLeft: 10,
        paddingRight: 10,
        transformOrigin: 'left center',
        fontSize: '14px',
        fontWeight: '600',
        letterSpacing: '0.05em',
      }}
    >
      {label}
    </label>
  );
};

const ScreenMasterForm = ({ isOpen, onClose, handleSave, onSave: onSaveProp, isEditMode, editData }) => {
  const { companyList, userGlobalSettingsData, company } = useGlobal();
  const shouldSendCompanyidItemScreen = userGlobalSettingsData?.[0]?.askCompany?.itemScreen || false;
  const shouldSendCompanyidItem = userGlobalSettingsData?.[0]?.askCompany?.item || false;
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const dispatch = useDispatch();
  const { user } = useUser();

  const initialState = {
    name: '',
    itemId: '',
    cut: '',
    itemRate: '',
    notes: '',
    companyIds: [],
    company: '',
  };

  const initialErrors = {
    name: '',
    itemId: '',
  };

  const [currentRow, setCurrentRow] = useState(initialState);
  const [focusedInput, setFocusedInput] = useState('');
  const [errors, setErrors] = useState(initialErrors);

  const textColor = useColorModeValue('gray.800', 'white');
  const inputBg = useColorModeValue('gray.100', '#3a4a61');
  const modalBg = useColorModeValue('white', '#2d3748');
  const borderColor = useColorModeValue('gray.200', '#4a5568');

  const itemData = useSelector((state) => state.item.list);

  // ─── Refs ────────────────────────────────────────────────────────────────────
  const nameRef = useRef();
  const itemIdRef = useRef();
  const cutRef = useRef();
  const itemRateRef = useRef();
  const notesRef = useRef();
  const saveRef = useRef();
  const companyRef = useRef();

  // ─── FIX 1: Dynamic fieldOrder based on shouldSendCompanyidItemScreen ────────
  // This ensures Tab/Shift+Tab skips companyRef when the field is hidden
  const fieldOrder = useMemo(() => {
    const order = [nameRef, itemIdRef];
    if (shouldSendCompanyidItemScreen) order.push(companyRef);
    order.push(cutRef, itemRateRef, notesRef, saveRef);
    return order;
  }, [shouldSendCompanyidItemScreen]);

  const focusNext = useCallback((ref) => {
    const idx = fieldOrder.indexOf(ref);
    if (idx !== -1 && idx < fieldOrder.length - 1) {
      fieldOrder[idx + 1].current?.focus();
    }
  }, [fieldOrder]);

  const focusPrev = useCallback((ref) => {
    const idx = fieldOrder.indexOf(ref);
    if (idx > 0) {
      fieldOrder[idx - 1].current?.focus();
    }
  }, [fieldOrder]);

  const handleCompanyChange = useCallback((selectedOptions) => {
    setCurrentRow((prev) => ({
      ...prev,
      companyIds: selectedOptions ?? [],
    }));
  }, []);

  const dbName = company?.dbName;

  const companyOptions = useMemo(() => {
    return getShareCompanyOptions({ dbName, company: companyList });
  }, [companyList, dbName]);

  const lastcompanyOptions = useMemo(
    () =>
      companyOptions.map((company) => ({
        value: company.value,
        label: company.label,
        id: company.value,
        name: company.label,
      })),
    [companyOptions],
  );

  // ─── Load & pre-fill ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      dispatch(
        getItem({
          companyId: user?.loginhistory?.companyId,
          ...(shouldSendCompanyidItem && { companyIds: user?.loginhistory?.companyId }),
        }),
      );
      setErrors(initialErrors);

      if (isEditMode && editData) {
        setCurrentRow({
          name: editData.name || '',
          itemId: editData.itemId || '',
          cut: editData.cut ?? '',
          itemRate: editData.itemRate ?? '',
          notes: editData.notes || '',
          companyIds: editData.companyIds || [],
        });
      } else {
        setCurrentRow(initialState);
      }
    }
  }, [dispatch, isOpen, isEditMode, editData, user?.loginhistory?.companyId]);

  // Auto-focus name on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => nameRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Global Escape → close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleFocus = (id) => setFocusedInput(id);
  const handleBlur = () => setFocusedInput('');

  const handleChange = (field, value) => {
    setCurrentRow((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // ─── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    const newErrors = { name: '', itemId: '' };
    let isValid = true;

    if (!currentRow.name?.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }
    if (!currentRow.itemId) {
      newErrors.itemId = 'Item is required';
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      if (newErrors.name) nameRef.current?.focus();
      else if (newErrors.itemId) itemIdRef.current?.focus();
    }

    return isValid;
  };

  const onSave = async () => {
    if (!validate()) return;

    const compIdsArray =
      shouldSendCompanyidItemScreen && Array.isArray(currentRow.companyIds)
        ? currentRow.companyIds.map((opt) => opt?.value ?? opt?.id ?? opt)
        : undefined;

    let response;

    if (isEditMode && (editData?.id || editData?._id)) {
      const payload = {
        id: editData?.id || editData?._id,
        name: currentRow.name,
        itemId: Number(currentRow.itemId),
        ...(currentRow.cut !== '' && { cut: Number(currentRow.cut) }),
        ...(currentRow.itemRate !== '' && { itemRate: Number(currentRow.itemRate) }),
        ...(currentRow.notes && { notes: currentRow.notes }),
        ...(compIdsArray && { companyIds: compIdsArray }),
      };
      response = await dispatch(updateScreen(payload));
    } else {
      const payload = {
        name: currentRow.name,
        itemId: Number(currentRow.itemId),
        ...(currentRow.cut !== '' && { cut: Number(currentRow.cut) }),
        ...(currentRow.itemRate !== '' && { itemRate: Number(currentRow.itemRate) }),
        ...(currentRow.notes && { notes: currentRow.notes }),
        ...(compIdsArray && { companyIds: compIdsArray }),
      };
      response = await dispatch(addScreen(payload));
    }

    // ✅ Aapke response format ke hisab se check
    if (response?.data?.code === 1) {
      const saveFn = handleSave || onSaveProp;
      if (typeof saveFn === 'function') saveFn(currentRow);
      onClose(); // ✅ Success pe modal band
    }
    // Error pe modal open rahega automatically
  };

  const makeKeyDown = useCallback((currentRef) => (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      focusPrev(currentRef);
    } else if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      focusNext(currentRef);
    }
  }, [focusNext, focusPrev, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(4px)" />
      <ModalContent
        bg={modalBg}
        color={textColor}
        borderRadius="20px"
        p={0}
        mx="15px"
        boxShadow="0 25px 50px rgba(0,0,0,0.25)"
        border="1px solid"
        borderColor={borderColor}
      >
        <ModalHeader
          fontSize="20px"
          fontWeight="700"
          mb={{ base: 0, xl: 2 }}
          pt={6}
          pb={2}
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          {isEditMode ? '✏️ Edit Screen' : '➕ Add New Screen'}
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />

        <ModalBody py={6}>
          <Grid
            templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
            gap={6}
          >

            {/* ── NAME * ── */}
            <GridItem>
              <FormControl isRequired isInvalid={!!errors.name}>
                {DynamicInput(
                  'name',
                  'Name',
                  {
                    value: currentRow.name,
                    ref: nameRef,
                    onChange: (e) => handleChange('name', e.target.value),
                    onKeyDown: makeKeyDown(nameRef),
                  },
                  errors.name,
                  textColor,
                  'text',
                  true,
                )}
                {errors.name && (
                  <FormErrorMessage mt={1} ml={2} fontSize="12px">
                    {errors.name}
                  </FormErrorMessage>
                )}
              </FormControl>
            </GridItem>

            {/* ── ITEM * ── */}
            <GridItem>
              <FormControl isRequired isInvalid={!!errors.itemId}>
                {renderDynamicDropdown(
                  'itemId',
                  'Item',
                  itemData,
                  {
                    value: currentRow.itemId,
                    ref: itemIdRef,
                    onChange: (value) => handleChange('itemId', value),
                    showCustomNoOptions: true,
                    openfrom: 'item',
                    onKeyDown: makeKeyDown(itemIdRef),
                  },
                  errors.itemId,
                  textColor,
                  true,
                  'Select Item',
                )}
                {errors.itemId && (
                  <FormErrorMessage mt={1} ml={2} fontSize="12px">
                    {errors.itemId}
                  </FormErrorMessage>
                )}
              </FormControl>
            </GridItem>

            {/* ── COMPANY ── */}
            {shouldSendCompanyidItemScreen && (
              <GridItem>
                {renderDynamicUiDropdown(
                  'company',
                  'Company',
                  lastcompanyOptions,
                  {
                    ref: companyRef,
                    value: currentRow.companyIds,
                    onChange: handleCompanyChange,
                    onKeyDown: makeKeyDown(companyRef),
                  },
                  {},
                  textColor,
                  false,
                  'Select company',
                  true,
                  undefined,
                  true,
                )}
              </GridItem>
            )}

            {/* ── CUT ── */}
            <GridItem>
              <FormControl>
                {DynamicInput(
                  'cut',
                  'Cut',
                  {
                    value: currentRow.cut,
                    ref: cutRef,
                    onChange: (e) => handleChange('cut', e.target.value),
                    onKeyDown: makeKeyDown(cutRef),
                  },
                  '',
                  textColor,
                  'number',
                  false,
                )}
              </FormControl>
            </GridItem>

            {/* ── ITEM RATE ── */}
            <GridItem>
              <FormControl>
                {DynamicInput(
                  'itemRate',
                  'Item Rate',
                  {
                    value: currentRow.itemRate,
                    ref: itemRateRef,
                    onChange: (e) => handleChange('itemRate', e.target.value),
                    onKeyDown: makeKeyDown(itemRateRef),
                  },
                  '',
                  textColor,
                  'number',
                  false,
                )}
              </FormControl>
            </GridItem>

            {/* ── NOTES — full width ── */}
            <GridItem colSpan={{ base: 1, md: 2 }}>
              <FormControl>
                <div style={{ position: 'relative', marginTop: '5px' }}>
                  <Textarea
                    id="notes"
                    ref={notesRef}
                    value={currentRow.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    onFocus={() => handleFocus('notes')}
                    onBlur={handleBlur}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        onClose();
                      } else if (e.key === 'Tab' && e.shiftKey) {
                        e.preventDefault();
                        focusPrev(notesRef);
                      } else if (e.key === 'Tab') {
                        e.preventDefault();
                        focusNext(notesRef);
                      }
                      // Enter inside Textarea = new line (default behavior kept)
                    }}
                    color={textColor}
                    bg={inputBg}
                    borderRadius="12px"
                    _placeholder={{ color: 'transparent' }}
                    fontSize="15px"
                    minHeight="90px"
                    px="20px"
                    pt="20px"
                    border="1.5px solid"
                    borderColor={borderColor}
                    _focus={{
                      borderColor: 'blue.400',
                      boxShadow: '0 0 0 1px #63b3ed',
                      bg: isDark ? '#3a4a61' : 'white',
                    }}
                    resize="vertical"
                  />
                  <FloatingLabel
                    id="notes"
                    label="NOTES"
                    isFocused={focusedInput === 'notes'}
                    hasValue={!!currentRow.notes}
                    colorMode={colorMode}
                  />
                </div>
              </FormControl>
            </GridItem>

          </Grid>

          <Text fontSize="12px" color="gray.400" mt={3}>
            * Required fields
          </Text>
        </ModalBody>

        <ModalFooter
          borderTop="1px solid"
          borderColor={borderColor}
          gap={3}
          pt={4}
        >
          {/* ── Save / Update Button ── */}
          <button
            ref={saveRef}
            onClick={onSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSave();
              } else if (e.key === 'Tab' && e.shiftKey) {
                e.preventDefault();
                focusPrev(saveRef);
              } else if (e.key === 'Escape') {
                onClose();
              }
              // Tab forward from Save button goes to browser default (outside modal)
            }}
            className={`group relative flex items-center justify-center gap-3 py-2 px-7 rounded-xl font-medium shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-1 focus:outline-none focus:ring-4 active:scale-95 overflow-hidden border ${isDark
              ? 'bg-gradient-to-br from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white shadow-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-400/40 focus:ring-cyan-200/50 border-cyan-300/30'
              : 'bg-gradient-to-br from-emerald-400 to-teal-500 hover:from-emerald-300 hover:to-teal-400 text-white shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-400/40 focus:ring-emerald-200/70 border-emerald-300/50'
              }`}
          >
            {/* Shimmer overlay */}
            <span
              className={`absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out ${isDark ? 'via-cyan-100/20' : 'via-white/30'
                }`}
            />
            {/* Icon */}
            <span
              className={`relative flex items-center justify-center w-6 h-6 rounded-lg backdrop-blur-sm transition-all duration-300 ${isDark
                ? 'bg-cyan-100/20 group-hover:bg-cyan-100/30'
                : 'bg-white/20 group-hover:bg-white/30'
                }`}
            >
              {isEditMode ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                </svg>
              )}
            </span>
            <span className="relative text-base font-semibold">
              {isEditMode ? 'Update' : 'Save'}
            </span>
          </button>

          {/* ── Cancel Button ── */}
          <button
            onClick={onClose}
            className={`group relative flex items-center justify-center gap-2 py-2 px-7 rounded-xl font-medium transition-all duration-300 ease-out transform hover:-translate-y-1 focus:outline-none focus:ring-4 active:scale-95 border ${isDark
              ? 'bg-transparent hover:bg-white/10 text-gray-300 hover:text-white border-gray-600 hover:border-gray-400 focus:ring-gray-500/30'
              : 'bg-transparent hover:bg-gray-100 text-gray-600 hover:text-gray-800 border-gray-300 hover:border-gray-400 focus:ring-gray-200/70'
              }`}
          >
            <span className="relative text-base font-semibold">Cancel</span>
          </button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ScreenMasterForm;